import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Image,
  PDFViewer,
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import { useEffect, useState } from "react";
import { Button, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useCompany } from "../hooks/useCompany";

// Create styles for the PDF
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    padding: 20,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: "2px solid #000",
  },
  logo: {
    width: 200,
    marginRight: 10,
    objectFit: "contain",
  },
  reference: {
    fontSize: 50,
    fontWeight: "bold",
  },
  contentContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  topSection: {
    flexDirection: "row",
    marginBottom: 20,
  },
  companyInfo: {
    width: "50%",
    paddingRight: 20,
  },
  companyName: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 10,
  },
  productName: {
    fontSize: 40,
    marginBottom: 5,
  },
  detailsContainer: {
    width: "50%",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  detailItem: {
    width: "50%",
    marginBottom: 10,
  },
  detailLabel: {
    fontSize: 32,
    color: "#666",
  },
  detailValue: {
    fontSize: 40,
    fontWeight: "bold",
  },
  barcodeSection: {
    alignItems: "center",
    marginTop: 10,
  },
  barcode: {
    width: "70%",
    height: 100,
  },
  eanText: {
    fontSize: 36,
    fontWeight: "bold",
    marginTop: 10,
    textAlign: "center",
  },
  // Styles for error message
  errorContainer: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
  },
  errorMessage: {
    fontSize: 10,
    textAlign: "center",
  },
});

interface ShippingLabelProps {
  booking: {
    id: number;
    reference: string;
    transport_type: { name: string; abbreviation: string };
    booking_type?: { name: string };
    booking_incoterm: { name: string };
    origin_port?: { name: string; country: string };
    destination_port?: { name: string; country: string };
    supplier: {
      name: string;
      email: string;
      phone: string;
      country: string;
      city: string;
      address: string;
    };
  };
  product: {
    id: number;
    name: string;
    hs_code: string | null;
    gross_weight: number;
    cbm: number;
    pieces?: number | null;
    cartons?: number | null;
    ean: number | null;
  };
}

// Define the product with barcode type
export interface ProductWithBarcode {
  id: number;
  name: string;
  hs_code: string | null;
  gross_weight: number;
  cbm: number;
  pieces?: number | null;
  cartons?: number | null;
  ean: number | null;
  barcodeBase64?: string | null;
}

/** Absolute URL so @react-pdf can fetch and embed the logo when generating blobs (same as app origin + Vite base). */
function shippingLabelLogoSrc(): string {
  if (typeof window === "undefined") {
    return "/logo_black.png";
  }
  const base = import.meta.env.BASE_URL || "/";
  return new URL("logo_black.png", `${window.location.origin}${base}`).href;
}

// Create the PDF Document component
export const ShippingLabelDocument = ({
  booking,
  product,
  companyName,
}: {
  booking: ShippingLabelProps["booking"];
  product: ProductWithBarcode;
  companyName: string;
}) => {
  const logoSrc = shippingLabelLogoSrc();

  // Check if we have all required data
  const hasRequiredData = booking && companyName && product && product.name;

  // Validate required data
  if (!hasRequiredData) {
    return (
      <Document>
        <Page
          size="A4"
          orientation="landscape"
          style={styles.page}
          wrap={false}
        >
          <View style={styles.errorContainer}>
            <Text style={styles.errorTitle}>Missing Required Data</Text>
            <Text style={styles.errorMessage}>
              Some required information is missing.
            </Text>
          </View>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap={false}
      >
        <View style={styles.header}>
          <Image src={logoSrc} style={styles.logo} />
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={{ fontSize: 40 }}>EFF</Text>
            <Text style={{ fontSize: 40, fontWeight: "bold" }}>
              {booking.id}
            </Text>
            <Text style={{ fontSize: 40 }}>
              {booking.transport_type?.abbreviation || ""}
            </Text>
          </View>
        </View>

        <View style={styles.contentContainer}>
          <View style={styles.topSection}>
            <View style={styles.companyInfo}>
              <Text style={styles.companyName}>{companyName}</Text>
              <Text style={styles.productName}>{product.name}</Text>
            </View>

            <View style={styles.detailsContainer}>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Total Pieces</Text>
                <Text style={styles.detailValue}>
                  {product.pieces || "N/A"}
                </Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Total Cartons</Text>
                <Text style={styles.detailValue}>
                  {product.cartons || "N/A"}
                </Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Total Weight</Text>
                <Text style={styles.detailValue}>
                  {product.gross_weight.toFixed(2)} kg
                </Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Total Volume</Text>
                <Text style={styles.detailValue}>
                  {product.cbm.toFixed(2)} CBM
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.barcodeSection}>
            {product.ean && product.barcodeBase64 ? (
              <>
                <Image src={product.barcodeBase64} style={styles.barcode} />
                <Text style={styles.eanText} wrap={false}>
                  {product.ean}
                </Text>
              </>
            ) : (
              <Text style={styles.eanText} wrap={false}>
                No barcode available
              </Text>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const ShippingLabel = ({ booking, product }: ShippingLabelProps) => {
  const [barcodeBase64, setBarcodeBase64] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const { companyDetails } = useCompany();
  const companyName = companyDetails?.name || "Your Company";

  // Add debug logging for props
  console.log("ShippingLabel received props:", { booking, product });

  useEffect(() => {
    console.log("useEffect triggered with product:", product);

    const generateBarcode = () => {
      console.log("Attempting to generate barcode for product:", product);

      if (!product) {
        console.log("No product data available");
        setBarcodeError("No product data available");
        setIsGenerating(false);
        return;
      }

      if (!product.ean) {
        console.log("No EAN available for product:", product.name);
        setBarcodeError("No EAN available for this product");
        setIsGenerating(false);
        return;
      }

      try {
        console.log("Creating barcode for EAN:", product.ean);
        // Create a canvas element
        const canvas = window.document.createElement("canvas");
        // Set canvas size - wider and higher resolution for better quality
        canvas.width = 800;
        canvas.height = 200;

        // Use the EAN number as is (consistent with utils/shippingLabel.ts)
        const barcodeValue = product.ean.toString();
        console.log("Using barcode value:", barcodeValue);

        // Generate barcode using JsBarcode with CODE128 format (no validation)
        JsBarcode(canvas, barcodeValue, {
          format: "CODE128",
          width: 2,
          height: 100,
          displayValue: true,
          fontSize: 20,
          margin: 10,
          background: "#ffffff",
          lineColor: "#000000",
          flat: true,
          textAlign: "center",
          textPosition: "bottom",
          textMargin: 6,
        });

        // Log the canvas state
        console.log(
          "Canvas created with dimensions:",
          canvas.width,
          "x",
          canvas.height
        );

        // Convert to base64 with proper encoding
        const base64 = canvas.toDataURL("image/png", 1.0);
        console.log("Base64 string length:", base64 ? base64.length : 0);

        if (base64) {
          console.log("Successfully generated barcode");
          setBarcodeBase64(base64);
          setBarcodeError(null);
        } else {
          console.error("Failed to generate barcode image - empty base64");
          setBarcodeError("Failed to generate barcode image");
        }
      } catch (err) {
        console.error("Error generating barcode:", err);
        setBarcodeError(`Error generating barcode: ${err}`);
      } finally {
        setIsGenerating(false);
      }
    };

    generateBarcode();
  }, [product]);

  // Create a modified product object with the barcode
  const productWithBarcode = {
    ...product,
    barcodeBase64,
  };

  // Add debug logging for the final product object
  console.log("Final product with barcode:", productWithBarcode);

  if (isGenerating) {
    return (
      <div style={{ textAlign: "center", padding: "10px" }}>
        <Spin size="small" />
        <p style={{ fontSize: "12px", margin: "5px 0 0" }}>
          Generating label...
        </p>
      </div>
    );
  }

  // Create the document once
  const document = (
    <ShippingLabelDocument
      booking={booking}
      product={productWithBarcode}
      companyName={companyName}
    />
  );

  return (
    <div style={{ textAlign: "center" }}>
      {barcodeError && product?.ean && (
        <div style={{ color: "red", marginBottom: "10px", fontSize: "12px" }}>
          Warning: {barcodeError}
        </div>
      )}
      {/* Use PDFViewer for the preview with adjusted styling for landscape */}
      <div
        style={{
          marginBottom: "15px",
          height: "180px", // Reduced height
          width: "100%",
          overflow: "hidden",
          border: "1px solid #ddd",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <PDFViewer
          width="100%"
          height="100%"
          showToolbar={false}
          style={{
            border: "none",
          }}
        >
          {document}
        </PDFViewer>
      </div>

      {/* Download link with icon */}
      <PDFDownloadLink
        document={document}
        fileName={`shipping-label-${booking.id}-${product.id}.pdf`}
        style={{
          textDecoration: "none",
        }}
      >
        {({ loading }) => (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={loading}
            size="small"
            style={{
              fontSize: "12px",
              height: "32px",
              backgroundColor: "#1890ff",
              borderColor: "#1890ff",
            }}
          >
            {loading ? "Preparing..." : "Download Shipping Label"}
          </Button>
        )}
      </PDFDownloadLink>
    </div>
  );
};
